<template>
  <div class="edit-view display-in-center view" v-loading.fullscreen="loading || !isReadyToScan">
    <div class="step-label">
      {{ $t('barcode_link') }}
    </div>
    <div class="barcode-scanner relative">
      <div id="scan-area">
        <div class="scan-selection-camera" @click="openDialog" v-show="cameras.length > 0">
          <IconSwitch />
        </div>
        <div id="video-area">
          <video playsinline autoplay id="video"></video>
        </div>
      </div>

      <BarcodeSymbologiesSelection
        class="absolute bottom-2 z-50 left-2 flex w-52"
        @update:onSymbologiesChange="setBarcodeSymbologies"
        v-model:symbologies="symbologies"
      />

      <ScanArea />
    </div>
    <div class="barcode-number-block">
      <span> {{ $t('Number of Scanned Barcodes') }} </span>
      <span class="label-box" @click="onCheckScannedBarcode">
        {{ scannedBarcodeList.length }}
      </span>
    </div>
    <div v-if="estimateLinkedCount > 0" class="barcode-number-block">
      <span>
        {{ $t('Number of reserved linked barcode') }}
      </span>
      <span class="label-box">
        {{ estimateLinkedCount }}
      </span>
    </div>

    <el-button
      circle
      :disabled="scannedBarcodeList.length === 0"
      type="primary"
      class="submit-button"
      @click="submit()"
    >
      {{ $t('OK') }}
    </el-button>
  </div>
  <el-dialog
    :lock-scroll="true"
    center
    v-model="dialogVisible"
    width="90%"
    top="0"
    :show-close="false"
    :destroy-on-close="false"
    :title="checkScannedBarcode ? $t('Scanned Barcodes') : ''"
  >
    <template v-if="checkScannedBarcode">
      <p v-if="scannedBarcodeList.length === 0">
        {{ $t('no barcodes') }}
      </p>
      <BarcodeLabelList v-else :barcodes="scannedBarcodeList || []" />
    </template>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="onCloseDialog">
          {{ $t('OK') }}
        </el-button>
      </span>
    </template>
  </el-dialog>
  <SelectionCameraPopup v-model:selectedCamera="selectedCamera" :cameras="cameras" v-model:isShow="isOpenDialog" />
</template>

<script lang="ts">
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import MultipleBarcodeScanMixin from '@/components/mixins/MultipleBarcodeScanMixin.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import { BE_LINKED, FETCH_BARCODE, LINK_TO, SAVE_SCANNED_BARCODES } from '@/store/actions'
import { getEstimateBarcodeLinked, linkingBarcodes } from '@/utils/api'
import { ELinkType } from '@/utils/enums'
import { mixins, Options } from 'vue-class-component'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import { IBarcode } from '@/utils/types/barcodeTypes'
import { IBarcodeDefinitionType } from '@/utils/types/fieldDefinition'

type TLinkType = {
  type: ELinkType
  label: string
  operation: typeof LINK_TO | typeof BE_LINKED
}

@Options({
  components: {
    ScanArea,
    BarcodeLabelList,
    BarcodeSymbologiesSelection,
    SelectionCameraPopup,
    IconSwitch,
  },
  name: 'BarcodeLinkScan',
})
export default class BarcodeLinkScan extends mixins(MultipleBarcodeScanMixin) {
  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  children = [] as IBarcode[]
  estimateLinkedCount = 0
  isReadyToScan = true

  async created() {
    this.isForcedHideMultipleScan = true
    this.loading = true
    await this.getBarcodeInfo()
    const { count } = await getEstimateBarcodeLinked(this.$route.params.barcodeId)
    this.estimateLinkedCount = count
    this.loading = false
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    await this.onLeaveGuard(to, from, next)
  }

  async getBarcodeInfo() {
    if (this.$route.params?.barcodeId && this.$route.params?.project) {
      await this.$store.dispatch(FETCH_BARCODE, { id: this.$route.params?.barcodeId })
    }
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  barcodeTypeInfo(barcodeType: string): Record<string, IBarcodeDefinitionType> {
    return this.$store.state.project?.details?.barcodeTypes[barcodeType]
  }

  async validateBarcode(resultScanned: IBarcode, listScannedResult?: IBarcode[]) {
    if (this.barcode.id === resultScanned.id) throw this.$t('barcode_linking_by_itself_error')

    const currentProjectId = this.$store.state.project.details.id
    const scannedBCProjectId = resultScanned.projectId
    if (currentProjectId !== scannedBCProjectId) throw this.$t('scanned_barcode_not_same_project')

    if (!resultScanned.isActivated) throw this.$t('barcode not activated')

    const bcTypeInfo = this.barcodeTypeInfo(resultScanned.barcodeType)
    if (!bcTypeInfo.allowToBeLinked) throw this.$t('barcode_linking_not_allow')

    const listScannedId = Array.from(
      new Set([resultScanned?.id, ...(listScannedResult || []).map((result) => result?.id)])
    )

    const data = {
      barcodeId: this.barcode?.id,
      targetBarcodeIds: listScannedId,
      isDryRun: true,
    }

    await linkingBarcodes(this.linkType.type, data)
  }

  get linkType(): TLinkType {
    return this.$route.name === `${ELinkType.LINK_TO}-scan`
      ? { type: ELinkType.LINK_TO, label: this.$t('linking.link_to_read_result'), operation: LINK_TO }
      : { type: ELinkType.BE_LINKED, label: this.$t('linking.be_linked_read_result'), operation: BE_LINKED }
  }

  forward() {
    this.$store.dispatch(SAVE_SCANNED_BARCODES, this.barcodes)
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.linkType.label,
        operation: this.linkType.operation,
      },
    })
  }

  async handleCompleteScan() {
    if (!this.isMultipleScan || this.valueCode.trim() === '') {
      return
    }

    this.handleGotScanBarcode(this.valueCode)
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.edit-view.view {
  padding-left: 0;
  padding-right: 0;
}

.step-label {
  margin-bottom: 25px;
}

.barcode-scanner {
  height: 430px;
  background: rgb(32, 32, 32);
  position: relative;
}

.barcode-scanner video {
  width: 100%;
  height: 100%;
}

.scan-selection-camera {
  position: absolute;
  top: 0.5em;
  left: 0.5em;
}
</style>
